import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { usePublicSettings } from "@/hooks/useSettings";
import { expertService } from "@/services/expertService";
import { apiError } from "@/lib/api";
import { ExpertCard } from "@/components/ExpertCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Sparkles, Loader2, Crown, Lock } from "lucide-react";

const SUGGESTIONS = ["Quantum computing", "Stoicism", "Behavioral economics", "Jazz improvisation"];

function ScholarGate({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-emerald-500/30" data-testid="scholar-gate-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <Lock className="h-5 w-5 text-emerald-500" /> A Scholar feature
          </DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          Collecting experts and generating deep-dive expert courses is part of <b>Scholar</b>. Sign up to build your
          personal roster of experts and turn any one of them into a narrated 5-module masterclass.
        </p>
        <div className="flex flex-col gap-2 mt-2">
          <Link to="/register" data-testid="gate-signup-button">
            <Button className="w-full rounded-none bg-emerald-500 hover:bg-emerald-400 text-[#04110b] gap-2">
              <Crown className="h-4 w-4" /> Become a Scholar
            </Button>
          </Link>
          <Link to="/pricing" className="text-center text-xs text-muted-foreground hover:text-foreground">See plans & pricing</Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ExpertSearch() {
  const { user } = useAuth();
  const { data: settings } = usePublicSettings();
  const xm = settings?.expertmeai || {};
  const brandName = xm.brand_name || "ExpertMeAi";
  const tagline = xm.tagline || "Authentic mentorship from the world's greatest minds.";
  const isMember = user?.plan_type === "scholar" || user?.plan_type === "enterprise" || user?.role === "admin";
  const [topic, setTopic] = useState("");
  const [searched, setSearched] = useState("");
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const run = async (t) => {
    const q = (t ?? topic).trim();
    if (!q) return;
    setLoading(true); setExperts([]);
    try {
      const res = await expertService.search(q);
      setExperts(res.experts || []);
      setSearched(q);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ScholarGate open={gateOpen} onClose={setGateOpen} />

      <div className="bg-[#050505] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-14">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400 mb-3">
            <Sparkles className="h-4 w-4" /> {brandName}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mb-3">
            Meet the minds behind any subject.
          </h1>
          <p className="text-white/60 max-w-2xl mb-8">
            {tagline} Search a topic and our researcher surfaces six leading experts — with AI-estimated credibility, notable works,
            and a narrated introduction. Then turn any of them into a masterclass.
          </p>

          <div className="flex gap-2 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 bg-white/5 border border-white/15 focus-within:border-emerald-400 px-4 h-12">
              <Search className="h-4 w-4 text-white/40" />
              <input
                value={topic} onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()}
                placeholder="Research experts on…" data-testid="expert-search-input"
                className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30"
              />
            </div>
            <Button onClick={() => run()} disabled={loading} data-testid="expert-search-button"
              className="rounded-none h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-[#04110b] font-bold gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find experts
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap text-xs text-white/40">
            <span>Try:</span>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => { setTopic(s); run(s); }}
                className="underline underline-offset-2 hover:text-white transition-colors" data-testid={`expert-suggestion-${s}`}>{s}</button>
            ))}
          </div>

          {!isMember && (
            <p className="mt-6 inline-flex items-center gap-2 text-xs text-emerald-300/80 border border-emerald-500/20 bg-emerald-500/5 px-3 py-2">
              <Lock className="h-3.5 w-3.5" /> Guests can explore the expert grid. Collecting experts & building courses is a Scholar feature.
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-12 bg-[#050505]">
        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="expert-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-72 bg-white/5 border border-white/10 animate-pulse" />
            ))}
          </div>
        )}
        {!loading && experts.length > 0 && (
          <>
            <p className="text-white/60 text-sm mb-6" data-testid="expert-results-heading">
              6 experts on <span className="text-white font-semibold">{searched}</span>
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="expert-grid">
              {experts.map((e, i) => (
                <ExpertCard key={e.id} expert={e} topic={searched} index={i} isMember={isMember} onLocked={() => setGateOpen(true)} />
              ))}
            </div>
          </>
        )}
        {!loading && experts.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-20 text-white/40" data-testid="expert-empty">
            <Search className="h-8 w-8 mx-auto mb-3 opacity-50" />
            <p>Search any subject to reveal its leading experts.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
